import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Chart } from 'primereact/chart';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { ProgressSpinner } from 'primereact/progressspinner';
import { toast } from 'react-toastify';
import { handleGetRequest } from '../../service/GetTemplate';
import { endPoints } from '../../Config';
import { getPreferredLanguage } from '../../utilities/languagePreference';
import { translateUiText } from '../../utilities/siteTranslations';
import OverShortDialog from './OverShortDialog';
import { selectOverShortRows, summarizeOverShort } from './overShortDetail';

const toNumeric = (value) => {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const cleaned = String(value).replace(/[^0-9.-]/g, '');
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeToken = (value) => String(value || '').trim().toLowerCase();

const formatMoney = (n) => {
    const amount = toNumeric(n);
    return '$' + amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPct = (n) => (n != null ? `${n}%` : 'N/A');
const isChiriquiRoute = (routeName) => String(routeName || '').toLowerCase().includes('chiriqui');
const firstText = (...values) => values.find((value) => value !== null && value !== undefined && String(value).trim() !== '') || '';

const formatDisplayTime = (value) => {
    if (!value || value === 'N/A' || value === '-') return value || '-';
    const raw = String(value).trim();
    if (/\b[AP]M\b/i.test(raw)) return raw.replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase());
    const match = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return raw;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour > 23 || minute > 59) return raw;
    const hour12 = hour % 12 || 12;
    const meridiem = hour < 12 ? 'AM' : 'PM';
    return `${hour12}:${String(minute).padStart(2, '0')} ${meridiem}`;
};

const formatTimeRange = (from, to, fallback = null) => {
    const fromDisplay = formatDisplayTime(from);
    const toDisplay = formatDisplayTime(to);
    if (fromDisplay && toDisplay && fromDisplay !== '-' && toDisplay !== '-') {
        return `${fromDisplay} → ${toDisplay}`;
    }
    return formatDisplayTime(fallback);
};

const formatDetailRange = (from, to, fallback = null) => {
    const fromDisplay = firstText(from);
    const toDisplay = firstText(to);
    if (fromDisplay && toDisplay) return `${fromDisplay} → ${toDisplay}`;
    return firstText(fallback, '-');
};

function formatDateForApi(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${m}/${d}/${date.getFullYear()}`;
}

function normalizeDate(value) {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : new Date(parsed.getTime());
}

function getYesterday() {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
}

function daysBetween(a, b) {
    const msPerDay = 86400000;
    return Math.round(Math.abs((b - a) / msPerDay)) + 1;
}

const ParlevelDashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [preferredLanguage, setPreferredLanguage] = useState(getPreferredLanguage());
    const appT = (value) => translateUiText(value, preferredLanguage);

    // Date range controls
    const [fromDate, setFromDate] = useState(getYesterday());
    const [toDate, setToDate] = useState(getYesterday());

    // Comparison controls
    const [compareEnabled, setCompareEnabled] = useState(false);
    const [compareFrom, setCompareFrom] = useState(null);
    const [compareTo, setCompareTo] = useState(null);

    // Data state
    const [loading, setLoading] = useState(false);
    const [dashboard, setDashboard] = useState(null);
    const [overShortOpen, setOverShortOpen] = useState(false);
    const dashboardRequest = useRef(0);
    const [driverFilter, setDriverFilter] = useState(null);
    const [routeFilter, setRouteFilter] = useState(null);
    const [machineInventory, setMachineInventory] = useState([]);
    const [changeLevelSummary, setChangeLevelSummary] = useState(null);

    // Trends state
    const [trendsData, setTrendsData] = useState(null);
    const [trendsLoading, setTrendsLoading] = useState(false);
    const autoGranularity = daysBetween(fromDate, toDate) <= 14 ? 'day' : daysBetween(fromDate, toDate) <= 60 ? 'week' : 'month';
    const [granularity, setGranularity] = useState(autoGranularity);

    // Route trend state
    const [routeNames, setRouteNames] = useState([]);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [routeTrendData, setRouteTrendData] = useState(null);
    const [routeTrendLoading, setRouteTrendLoading] = useState(false);
    const [routeGranularity, setRouteGranularity] = useState(autoGranularity);

    useEffect(() => {
        const sync = () => setPreferredLanguage(getPreferredLanguage());
        window.addEventListener('preferred-language-changed', sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener('preferred-language-changed', sync);
            window.removeEventListener('storage', sync);
        };
    }, []);

    const fetchDashboard = useCallback(async () => {
        const from = formatDateForApi(fromDate);
        const to = formatDateForApi(toDate);
        if (!from || !to) {
            toast.error(appT('Please select a valid date range'));
            return;
        }
        const requestId = ++dashboardRequest.current;
        setOverShortOpen(false);
        setLoading(true);
        try {
            let params = `from=${from}&to=${to}`;
            if (compareEnabled && compareFrom && compareTo) {
                const compareFromValue = formatDateForApi(compareFrom);
                const compareToValue = formatDateForApi(compareTo);
                if (compareFromValue && compareToValue) {
                    params += `&compareFrom=${compareFromValue}&compareTo=${compareToValue}`;
                }
            }
            const res = await handleGetRequest(endPoints.parlevelDashboard(params), false, false, dispatch);
            if (requestId !== dashboardRequest.current) return;
            if (res) {
                setDashboard(res?.data || res);
                setEfficiencyFilter(null);
                setDriverFilter(null);
                setRouteFilter(null);
            }
        } catch {
            if (requestId === dashboardRequest.current) toast.error('Failed to load dashboard');
        }
        if (requestId === dashboardRequest.current) setLoading(false);
    }, [dispatch, fromDate, toDate, compareEnabled, compareFrom, compareTo, preferredLanguage]);

    const fetchMachineInventory = useCallback(async () => {
        try {
            const res = await handleGetRequest(endPoints.parlevelMachineInventory(), false, false, dispatch);
            if (res) setMachineInventory(Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []);
        } catch { /* ignore */ }
    }, [dispatch]);

    const fetchChangeLevelSummary = useCallback(async () => {
        try {
            const res = await handleGetRequest(endPoints.parlevelChangeLevelsSummary, false, false, dispatch);
            if (res) setChangeLevelSummary(res?.data || res);
        } catch { /* keep the main dashboard available */ }
    }, [dispatch]);

    useEffect(() => {
        fetchMachineInventory();
        fetchChangeLevelSummary();
    }, [fetchMachineInventory, fetchChangeLevelSummary]);

    useEffect(() => {
        fetchDashboard();
        // Reset granularity based on new date range
        const safeFrom = normalizeDate(fromDate) || getYesterday();
        const safeTo = normalizeDate(toDate) || safeFrom;
        const days = daysBetween(safeFrom, safeTo);
        const autoGran = days <= 14 ? 'day' : days <= 60 ? 'week' : 'month';
        setGranularity(autoGran);
        setRouteGranularity(autoGran);
        setTrendsData(null);
        setRouteNames([]);
        setSelectedRoute(null);
        setRouteTrendData(null);
    }, [fetchDashboard]);

    const fetchTrends = useCallback(async (gran) => {
        setTrendsLoading(true);
        try {
            const g = gran || granularity;
            const params = `from=${formatDateForApi(fromDate)}&to=${formatDateForApi(toDate)}&groupBy=${g}`;
            const res = await handleGetRequest(endPoints.parlevelTrends(params), false, false, dispatch);
            if (res) setTrendsData(res?.data || res);
        } catch {
            toast.error('Failed to load trends');
        }
        setTrendsLoading(false);
    }, [dispatch, fromDate, toDate, granularity]);

    const fetchRouteNames = useCallback(async () => {
        try {
            const params = `from=${formatDateForApi(fromDate)}&to=${formatDateForApi(toDate)}`;
            const res = await handleGetRequest(endPoints.parlevelRouteNames(params), false, false, dispatch);
            if (res) {
                const names = res?.data || res;
                setRouteNames(Array.isArray(names) ? names : []);
            }
        } catch { /* ignore */ }
    }, [dispatch, fromDate, toDate]);

    const fetchRouteTrend = useCallback(async (route, gran) => {
        if (!route) return;
        setRouteTrendLoading(true);
        try {
            const g = gran || routeGranularity;
            const params = `from=${formatDateForApi(fromDate)}&to=${formatDateForApi(toDate)}&groupBy=${g}&route=${encodeURIComponent(route)}`;
            const res = await handleGetRequest(endPoints.parlevelRouteTrends(params), false, false, dispatch);
            if (res) setRouteTrendData(res?.data || res);
        } catch {
            toast.error('Failed to load route trends');
        }
        setRouteTrendLoading(false);
    }, [dispatch, fromDate, toDate, routeGranularity]);

    const summaryData = dashboard?.summary || {};
    const variance = dashboard?.variance || null;
    const comparison = dashboard?.comparison || null;
    const salesVariationPeriod = dashboard?.sales_variation_period || {};
    const dailyBreakdown = dashboard?.daily_breakdown || [];
    const dataNotices = dashboard?.data_notices || {};
    const hasRecentConsolidationLag = dataNotices?.consolidation_pending_recent === true;
    const missingConsolidationDates = Array.isArray(dataNotices?.missing_consolidation_dates)
        ? dataNotices.missing_consolidation_dates
        : [];

    // Raw data arrays (unfiltered)
    const rawRoutes = dashboard?.routes || [];
    const rawMachineDetail = dashboard?.machine_detail || [];
    const rawUnserviced = dashboard?.unserviced_machines || [];
    const rawLongWindowUnserviced = dashboard?.unvisited_unscheduled_15d || [];
    const rawTimeGaps = dashboard?.time_gaps || [];
    const rawAfterHours = dashboard?.after_hours || [];
    const rawDriverPerf = dashboard?.driver_performance || [];
    const rawSalesVariation = dashboard?.sales_variation || [];
    const rawRouteSalesDrops = dashboard?.route_sales_drops || [];
    const rawRouteOpt = dashboard?.route_optimization || {};
    const rawRouteOptRows = rawRouteOpt.rows || [];
    const complianceAlerts = dashboard?.compliance_alerts || [];

    // Build filter option lists from raw data
    const driverOptions = [...new Set([
        ...rawRoutes.map((r) => r.driver),
        ...rawDriverPerf.map((r) => r.driver_name),
        ...rawAfterHours.map((r) => r.driver),
        ...rawTimeGaps.map((r) => r.driver),
    ].filter((d) => d && d !== '-'))].sort();

    const routeOptions = [...new Set([
        ...rawRoutes.map((r) => r.route_name),
        ...rawMachineDetail.map((r) => r.route_name),
        ...rawUnserviced.map((r) => r.route_name),
    ].filter(Boolean))].sort();

    // Apply global filters
    const fD = (driver) => !driverFilter || normalizeToken(driver) === normalizeToken(driverFilter);
    const fR = (route) => !routeFilter || normalizeToken(route) === normalizeToken(routeFilter);

    const routes = rawRoutes.filter((r) => fD(r.driver) && fR(r.route_name));
    const machineDetail = rawMachineDetail.filter((r) => fR(r.route_name));
    const unserviced = rawUnserviced.filter((r) => fD(r.driver) && fR(r.route_name) && !isChiriquiRoute(r.route_name));
    const longWindowUnserviced = rawLongWindowUnserviced.filter((r) => fR(r.route_name) && !isChiriquiRoute(r.route_name));
    const timeGaps = rawTimeGaps.filter((r) => fD(r.driver) && fR(r.route_name));
    const afterHours = rawAfterHours.filter((r) => fD(r.driver) && fR(r.route_name));
    const driverPerf = rawDriverPerf.filter((r) => fD(r.driver_name));
    const salesVariation = rawSalesVariation.filter((r) => fR(r.route_name));
    const routeSalesDrops = rawRouteSalesDrops.filter((r) => fD(r.driver) && fR(r.route_name));
    const routeOptRows = rawRouteOptRows.filter((r) => {
        if (r.is_group) return fR(r.route_name) && (!driverFilter || rawRouteOptRows.some((d) => !d.is_group && d.route_name === r.route_name && fD(d.driver)));
        return fD(r.driver) && fR(r.route_name);
    });
    const routeOptSummary = rawRouteOpt.summary || {};
    const insights = dashboard?.insights || {};

    const OVERDUE_DAYS_THRESHOLD = 5;
    const todayKey = new Date().toDateString();
    const showLiveAlerts = toDate && new Date(toDate).toDateString() === todayKey || toDate >= new Date(new Date().setHours(0,0,0,0));
    const overdueMachines = showLiveAlerts
        ? machineInventory
            .filter((m) => m.days_since_service > OVERDUE_DAYS_THRESHOLD && fR(m.route))
            .sort((a, b) => b.days_since_service - a.days_since_service)
        : [];

    // Compute filtered summary when driver/route filter is active
    const hasFilter = !!(driverFilter || routeFilter);
    const filteredSummary = useMemo(() => {
        if (!hasFilter) return null;
        const hasRouteRows = routes.length > 0;
        const hasDriverRows = driverPerf.length > 0;
        if (!hasRouteRows && !hasDriverRows) return null;

        const totalSoldFromRoutes = routes.reduce((s, r) => s + toNumeric(r._raw_sold), 0);
        const totalOverShortFromRoutes = routes.reduce((s, r) => s + toNumeric(r._raw_over_short), 0);
        const totalCashless = routes.reduce((s, r) => s + toNumeric(r._raw_cashless), 0);
        const totalCollected = routes.reduce((s, r) => s + toNumeric(r._raw_collected), 0);
        const totalServicedFromRoutes = routes.reduce((s, r) => s + toNumeric(r.actual_machines), 0);
        const totalPlannedFromRoutes = routes.reduce((s, r) => s + toNumeric(r.planned_machines), 0);

        const totalSoldFromDrivers = driverPerf.reduce((s, d) => s + toNumeric(d._raw_sold ?? d.total_sold), 0);
        const totalOverShortFromDrivers = driverPerf.reduce((s, d) => s + toNumeric(d.total_over_short), 0);
        const totalServicedFromDrivers = driverPerf.reduce((s, d) => s + toNumeric(d.machines_serviced), 0);
        const totalPlannedFromDrivers = driverPerf.reduce((s, d) => s + toNumeric(d.machines_planned), 0);

        const shouldPreferDriverTotals = !!driverFilter && !routeFilter && hasDriverRows;
        const totalSold = shouldPreferDriverTotals ? totalSoldFromDrivers : totalSoldFromRoutes;
        const totalOverShort = shouldPreferDriverTotals ? totalOverShortFromDrivers : totalOverShortFromRoutes;
        const totalServiced = shouldPreferDriverTotals ? totalServicedFromDrivers : totalServicedFromRoutes;
        const totalPlanned = shouldPreferDriverTotals ? totalPlannedFromDrivers : totalPlannedFromRoutes;

        const compliancePcts = routes.filter((r) => toNumeric(r.planned_machines) > 0).map((r) => toNumeric(r.compliance_pct));
        const avgCompliance = compliancePcts.length > 0
            ? (compliancePcts.reduce((s, p) => s + p, 0) / compliancePcts.length).toFixed(1)
            : (totalPlanned > 0 ? ((totalServiced / totalPlanned) * 100).toFixed(1) : 0);
        // Avg par level from filtered driver performance
        const parLevels = driverPerf.map((d) => parseFloat(d.avg_par_level)).filter((n) => !isNaN(n));
        const avgParLevel = parLevels.length > 0
            ? (parLevels.reduce((a, b) => a + b, 0) / parLevels.length).toFixed(1)
            : summaryData.avg_par_level;
        // Avg hours from filtered routeOpt
        const filteredOptRows = routeOptRows.filter((r) => !r.is_group && r.duration_minutes > 0);
        const avgHours = filteredOptRows.length > 0
            ? (filteredOptRows.reduce((s, r) => s + r.duration_minutes, 0) / filteredOptRows.length / 60).toFixed(1)
            : 0;
        return {
            ...summaryData,
            total_sold: totalSold,
            total_cashless: shouldPreferDriverTotals ? driverPerf.reduce((s, d) => s + toNumeric(d.total_cashless), 0) : totalCashless,
            total_collected: shouldPreferDriverTotals ? driverPerf.reduce((s, d) => s + toNumeric(d.total_collected), 0) : totalCollected,
            total_over_short: totalOverShort,
            machines_serviced: totalServiced,
            machines_planned: totalPlanned,
            compliance_pct: Number(avgCompliance),
            avg_par_level: Number(avgParLevel),
            routes_active: hasRouteRows ? routes.length : summaryData.routes_active,
            unserviced_count: unserviced.length,
            avg_hours_per_route: Number(avgHours),
        };
    }, [hasFilter, routes, driverPerf, routeOptRows, unserviced, summaryData]);

    const overShortRows = useMemo(() => selectOverShortRows(dashboard?.over_short_detail, driverFilter, routeFilter), [dashboard, driverFilter, routeFilter]);
    const excludedWithoutRoute = routeFilter ? (selectOverShortRows(dashboard?.over_short_detail, driverFilter, null) || []).filter(row => !row.route_name).length : 0;
    const displaySummary = {
        ...(filteredSummary || summaryData),
        ...(overShortRows !== null ? { total_over_short: summarizeOverShort(overShortRows).net } : {}),
    };

    const isMultiDay = daysBetween(fromDate, toDate) > 1;

    // Merge Route Compliance + Route Efficiency into one dataset
    const routeEfficiency = dashboard?.route_efficiency || [];
    const mergedRoutes = useMemo(() => {
        const effMap = {};
        for (const e of routeEfficiency) effMap[e.route_name] = e;
        return routes.map((r) => {
            const eff = effMap[r.route_name] || {};
            return { ...r, overall_score: eff.overall_score ?? null, rating: eff.rating ?? null, avg_per_machine: eff.avg_per_machine ?? null, idle_pct: eff.idle_pct ?? null, sales_per_machine: eff.sales_per_machine ?? null };
        });
    }, [routes, routeEfficiency]);

    // Merge Time Gaps + After Hours into one Schedule Anomalies list
    const scheduleAnomalies = useMemo(() => {
        const gapRows = timeGaps.map((g) => ({
            _type: 'Gap',
            route_name: g.route_name,
            driver: g.driver,
            service_date: g.service_date || '-',
            time_display: formatTimeRange(g.from_time, g.to_time),
            detail: formatDetailRange(
                firstText(g.from_location_name, g.from_location, g.from_machine),
                firstText(g.to_location_name, g.to_location, g.to_machine),
                `${firstText(g.from_machine, '-')} → ${firstText(g.to_machine, '-')}`
            ),
            duration: g.gap_display,
            severity: g.gap_minutes >= 180 ? 'danger' : 'warning',
            severity_label: g.gap_minutes >= 180 ? 'Alto' : 'Moderado',
            _sort: g.gap_minutes || 0,
        }));
        const ahRows = afterHours.map((a) => ({
            _type: 'After Hours',
            route_name: a.route_name,
            driver: a.driver,
            service_date: a.service_date || '-',
            time_display: formatTimeRange(a.from_time, a.to_time, a.service_time),
            detail: formatDetailRange(
                firstText(a.from_location_name, a.from_location, a.from_machine),
                firstText(a.to_location_name, a.to_location, a.to_machine),
                `${firstText(a.machine_name, '-')} — ${firstText(a.location_name, '-')}`
            ),
            duration: '-',
            severity: a.severity === 'Crítico' || a.severity === 'Alto' ? 'danger' : a.severity === 'Moderado' ? 'warning' : 'info',
            severity_label: a.severity || 'Info',
            _sort: 0,
        }));
        return [...gapRows, ...ahRows];
    }, [timeGaps, afterHours]);

    const [efficiencyFilter, setEfficiencyFilter] = useState(null);

    const [expanded, setExpanded] = useState({ unservicedPeriod: true, unserviced: true, routeTrend: true });
    const toggleSection = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

    useEffect(() => {
        if (!dashboard || !isMultiDay || !expanded.routeTrend || routeNames.length > 0) return;
        fetchRouteNames();
    }, [dashboard, isMultiDay, expanded.routeTrend, routeNames.length, fetchRouteNames]);

    useEffect(() => {
        if (!dashboard || !isMultiDay || !routeFilter) return;
        setSelectedRoute(routeFilter);
        fetchRouteTrend(routeFilter);
    }, [dashboard, isMultiDay, routeFilter, fetchRouteTrend]);

    const complianceSeverity = (pct) => {
        if (pct == null) return 'info';
        if (pct >= 90) return 'success';
        if (pct >= 70) return 'warning';
        return 'danger';
    };

    const varianceColor = (val) => {
        if (val == null || val === 0) return '';
        return val > 0 ? 'text-green-600' : 'text-red-500';
    };

    const variancePrefix = (val) => {
        if (val == null) return '0';
        if (val > 0) return `+${val}`;
        return `${val}`;
    };

    // ── Trend chart data builders ─────────────────
    const CHART_COLORS = ['#0F8BFD', '#0BD18A', '#EC4DBC', '#FC6161', '#873EFE', '#00D0DE', '#EEE500', '#FF9F43', '#54A0FF', '#5F27CD'];
    const chartBase = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { usePointStyle: true } } } };

    const formatTrendLabel = (dateStr) => {
        if (!dateStr) return '';
        const [m, d, y] = dateStr.split('/');
        return `${m}/${d}`;
    };

    const salesChartData = useMemo(() => {
        if (!trendsData?.sales_trend?.length) return null;
        const labels = trendsData.sales_trend.map((s) => formatTrendLabel(s.date));
        return {
            labels,
            datasets: [
                { label: appT('POS Sales'), data: trendsData.sales_trend.map((s) => s.pos_sales), borderColor: '#0F8BFD', backgroundColor: 'rgba(15,139,253,0.1)', tension: 0.4, fill: true },
                { label: appT('Collected'), data: trendsData.sales_trend.map((s) => s.collected), borderColor: '#0BD18A', backgroundColor: 'transparent', tension: 0.4, borderDash: [5, 5] },
            ],
        };
    }, [trendsData, preferredLanguage]);

    const complianceChartData = useMemo(() => {
        if (!trendsData?.compliance_trend?.length) return null;
        const labels = trendsData.compliance_trend.map((c) => formatTrendLabel(c.date));
        const dataLen = labels.length;
        return {
            labels,
            datasets: [
                { label: appT('Compliance %'), data: trendsData.compliance_trend.map((c) => c.compliance_pct), borderColor: '#0BD18A', backgroundColor: 'rgba(11,209,138,0.1)', tension: 0.4, fill: true, pointRadius: 4 },
                { label: '90%', data: Array(dataLen).fill(90), borderColor: '#FC6161', borderDash: [6, 4], pointRadius: 0, borderWidth: 2, fill: false },
            ],
        };
    }, [trendsData, preferredLanguage]);

    const parLevelChartData = useMemo(() => {
        if (!trendsData?.compliance_trend?.length) return null;
        const labels = trendsData.compliance_trend.map((c) => formatTrendLabel(c.date));
        return {
            labels,
            datasets: [
                { label: appT('Avg Par Level %'), data: trendsData.compliance_trend.map((c) => c.avg_par_level), borderColor: '#873EFE', backgroundColor: 'rgba(135,62,254,0.1)', tension: 0.4, fill: true },
            ],
        };
    }, [trendsData, preferredLanguage]);

    const machinesChartData = useMemo(() => {
        if (!trendsData?.compliance_trend?.length) return null;
        const labels = trendsData.compliance_trend.map((c) => formatTrendLabel(c.date));
        return {
            labels,
            datasets: [
                { label: appT('Serviced'), data: trendsData.compliance_trend.map((c) => c.machines_serviced), backgroundColor: '#0F8BFD', borderRadius: 4 },
                { label: appT('Planned'), data: trendsData.compliance_trend.map((c) => c.machines_planned), backgroundColor: 'rgba(15,139,253,0.2)', borderRadius: 4 },
            ],
        };
    }, [trendsData, preferredLanguage]);

    const overShortChartData = useMemo(() => {
        if (!trendsData?.sales_trend?.length) return null;
        const labels = trendsData.sales_trend.map((s) => formatTrendLabel(s.date));
        const values = trendsData.sales_trend.map((s) => s.over_short);
        return {
            labels,
            datasets: [{
                label: appT('Over/Short'),
                data: values,
                borderColor: '#FC6161',
                backgroundColor: values.map((v) => v >= 0 ? 'rgba(11,209,138,0.3)' : 'rgba(252,97,97,0.3)'),
                tension: 0.4,
                fill: true,
                pointBackgroundColor: values.map((v) => v >= 0 ? '#0BD18A' : '#FC6161'),
            }],
        };
    }, [trendsData, preferredLanguage]);

    const driverChartData = useMemo(() => {
        if (!trendsData?.driver_trend?.length || !trendsData?.top_drivers?.length) return null;
        const labels = trendsData.driver_trend.map((d) => formatTrendLabel(d.date));
        const topN = trendsData.top_drivers.slice(0, 5);
        return {
            labels,
            datasets: topN.map((name, i) => ({
                label: name,
                data: trendsData.driver_trend.map((d) => d.drivers[name]?.sold || 0),
                borderColor: CHART_COLORS[i],
                backgroundColor: 'transparent',
                tension: 0.4,
                pointRadius: 3,
            })),
        };
    }, [trendsData]);

    // ── Route trend chart data builders ─────────────
    const routeSalesChartData = useMemo(() => {
        if (!routeTrendData?.trend?.length) return null;
        const labels = routeTrendData.trend.map((t) => formatTrendLabel(t.date));
        return {
            labels,
            datasets: [
                { label: appT('POS Sales'), data: routeTrendData.trend.map((t) => t.pos_sales), borderColor: '#0F8BFD', backgroundColor: 'rgba(15,139,253,0.1)', tension: 0.4, fill: true },
                { label: appT('Collected'), data: routeTrendData.trend.map((t) => t.collected), borderColor: '#0BD18A', backgroundColor: 'transparent', tension: 0.4, borderDash: [5, 5] },
            ],
        };
    }, [routeTrendData, preferredLanguage]);

    const routeOsChartData = useMemo(() => {
        if (!routeTrendData?.trend?.length) return null;
        const labels = routeTrendData.trend.map((t) => formatTrendLabel(t.date));
        const values = routeTrendData.trend.map((t) => t.over_short);
        return {
            labels,
            datasets: [{
                label: appT('Over/Short'),
                data: values,
                borderColor: '#FC6161',
                backgroundColor: values.map((v) => v >= 0 ? 'rgba(11,209,138,0.3)' : 'rgba(252,97,97,0.3)'),
                tension: 0.4,
                fill: true,
                pointBackgroundColor: values.map((v) => v >= 0 ? '#0BD18A' : '#FC6161'),
            }],
        };
    }, [routeTrendData, preferredLanguage]);

    const routeMachinesChartData = useMemo(() => {
        if (!routeTrendData?.trend?.length) return null;
        const labels = routeTrendData.trend.map((t) => formatTrendLabel(t.date));
        return {
            labels,
            datasets: [
                { label: appT('Serviced'), data: routeTrendData.trend.map((t) => t.machines_serviced), backgroundColor: '#0F8BFD', borderRadius: 4 },
                { label: appT('Planned'), data: routeTrendData.trend.map((t) => t.machines_planned), backgroundColor: 'rgba(15,139,253,0.2)', borderRadius: 4 },
            ],
        };
    }, [routeTrendData, preferredLanguage]);

    const routeParLevelChartData = useMemo(() => {
        if (!routeTrendData?.trend?.length) return null;
        const labels = routeTrendData.trend.map((t) => formatTrendLabel(t.date));
        return {
            labels,
            datasets: [
                { label: appT('Avg Par Level %'), data: routeTrendData.trend.map((t) => t.avg_par_level), borderColor: '#873EFE', backgroundColor: 'rgba(135,62,254,0.1)', tension: 0.4, fill: true },
            ],
        };
    }, [routeTrendData, preferredLanguage]);

    const pctChartOptions = { ...chartBase, scales: { y: { min: 60, max: 100, ticks: { callback: (v) => v + '%' } } } };
    const moneyChartOptions = { ...chartBase, scales: { y: { ticks: { callback: (v) => '$' + Number(v).toLocaleString() } } } };
    const countChartOptions = { ...chartBase };
    const osChartOptions = { ...chartBase, scales: { y: { ticks: { callback: (v) => '$' + v } } } };
    const granularityOptions = [{ label: appT('Daily'), value: 'day' }, { label: appT('Weekly'), value: 'week' }, { label: appT('Monthly'), value: 'month' }];

    return (
        <div className="grid">
            {/* -- Header --------------------------------- */}
            <div className="col-12 flex align-items-center justify-content-between flex-wrap">
                <div>
                    <h2 style={{ margin: 0 }}>
                        <i className="pi pi-chart-line mr-2" />
                        {appT('Parlevel Dashboard')}
                    </h2>
                    <p className="text-color-secondary mt-1">{appT('Operations monitoring and analytics')}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button
                        label={appT('Coin Tubes')}
                        icon="pi pi-dollar"
                        className="p-button-sm"
                        onClick={() => navigate('/parlevel/change-levels')}
                    />
                    <Button
                        label={appT('Data & Sync')}
                        icon="pi pi-database"
                        className="p-button-outlined p-button-sm"
                        onClick={() => navigate('/parlevel/data')}
                    />
                </div>
            </div>

            {/* -- Date Range Controls -------------------- */}
            <div className="col-12">
                <Card>
                    <div className="flex align-items-center gap-3 flex-wrap">
                        <label className="font-semibold text-sm">{appT('From')}:</label>
                        <Calendar
                            value={fromDate}
                            onChange={(e) => setFromDate(normalizeDate(e.value))}
                            dateFormat="mm/dd/yy"
                            showIcon
                            maxDate={new Date()}
                            className="w-11rem"
                        />
                        <label className="font-semibold text-sm">{appT('To')}:</label>
                        <Calendar
                            value={toDate}
                            onChange={(e) => setToDate(normalizeDate(e.value))}
                            dateFormat="mm/dd/yy"
                            showIcon
                            maxDate={new Date()}
                            className="w-11rem"
                        />

                        <div className="flex align-items-center gap-2 ml-3">
                            <Checkbox
                                inputId="compareToggle"
                                checked={compareEnabled}
                                onChange={(e) => setCompareEnabled(e.checked)}
                            />
                            <label htmlFor="compareToggle" className="font-semibold text-sm cursor-pointer">
                                Comparar con
                            </label>
                        </div>

                        {compareEnabled && (
                            <>
                                <label className="font-semibold text-sm">{appT('From')}:</label>
                                <Calendar
                                    value={compareFrom}
                                    onChange={(e) => setCompareFrom(normalizeDate(e.value))}
                                    dateFormat="mm/dd/yy"
                                    showIcon
                                    maxDate={new Date()}
                                    className="w-11rem"
                                />
                                <label className="font-semibold text-sm">{appT('To')}:</label>
                                <Calendar
                                    value={compareTo}
                                    onChange={(e) => setCompareTo(normalizeDate(e.value))}
                                    dateFormat="mm/dd/yy"
                                    showIcon
                                    maxDate={new Date()}
                                    className="w-11rem"
                                />
                            </>
                        )}

                        <Button
                            label={appT('Refresh')}
                            icon="pi pi-refresh"
                            className="p-button-sm"
                            onClick={fetchDashboard}
                            loading={loading}
                        />
                    </div>

                    {/* Global Filters */}
                    {dashboard && (driverOptions.length > 0 || routeOptions.length > 0) && (
                        <div className="flex align-items-center gap-3 flex-wrap mt-3 pt-3 border-top-1 surface-border">
                            <i className="pi pi-filter text-color-secondary" />
                            {driverOptions.length > 0 && (
                                <>
                                    <label className="font-semibold text-sm">{appT('Driver')}:</label>
                                    <Dropdown
                                        value={driverFilter}
                                        options={driverOptions.map((d) => ({ label: d, value: d }))}
                                        onChange={(e) => setDriverFilter(e.value)}
                                        placeholder={appT('All Drivers')}
                                        className="w-13rem"
                                        showClear
                                        filter
                                    />
                                </>
                            )}
                            {routeOptions.length > 0 && (
                                <>
                                    <label className="font-semibold text-sm">{appT('Route')}:</label>
                                    <Dropdown
                                        value={routeFilter}
                                        options={routeOptions.map((r) => ({ label: r, value: r }))}
                                        onChange={(e) => setRouteFilter(e.value)}
                                        placeholder={appT('All Routes')}
                                        className="w-13rem"
                                        showClear
                                        filter
                                    />
                                </>
                            )}
                            {(driverFilter || routeFilter) && (
                                <Button
                                    label={appT('Clear Filters')}
                                    icon="pi pi-times"
                                    className="p-button-text p-button-sm"
                                    onClick={() => { setDriverFilter(null); setRouteFilter(null); }}
                                />
                            )}
                        </div>
                    )}
                </Card>
            </div>

            {changeLevelSummary && (
                <div className="col-12">
                    <Card>
                        <div className="flex justify-content-between align-items-center flex-wrap gap-3">
                            <div>
                                <div className="font-semibold text-lg">
                                    <i className="pi pi-dollar mr-2 text-primary" />
                                    {appT('Change Availability')}
                                </div>
                                <small className="text-color-secondary">
                                    {changeLevelSummary.reporting_machines || 0} {appT('machines reporting coin tube data')}
                                </small>
                            </div>
                            <div className="flex gap-4 flex-wrap">
                                <div>
                                    <div className="text-color-secondary text-xs">{appT('Ready')}</div>
                                    <div className="text-xl font-bold text-green-600">{changeLevelSummary.by_status?.ready || 0}</div>
                                </div>
                                <div>
                                    <div className="text-color-secondary text-xs">{appT('Needs attention')}</div>
                                    <div className="text-xl font-bold text-orange-500">
                                        {(changeLevelSummary.by_status?.low || 0) +
                                            (changeLevelSummary.by_status?.critical || 0) +
                                            (changeLevelSummary.by_status?.stale || 0) +
                                            (changeLevelSummary.by_status?.data_issue || 0)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-color-secondary text-xs">{appT('Change float')}</div>
                                    <div className="text-xl font-bold">{formatMoney(changeLevelSummary.total_value)}</div>
                                </div>
                                <div>
                                    <div className="text-color-secondary text-xs">{appT('No tube data')}</div>
                                    <div className="text-xl font-bold">{changeLevelSummary.no_data_machines || 0}</div>
                                </div>
                            </div>
                            <Button
                                label={appT('View Coin Tubes')}
                                icon="pi pi-arrow-right"
                                iconPos="right"
                                className="p-button-outlined p-button-sm"
                                onClick={() => navigate('/parlevel/change-levels')}
                            />
                        </div>
                    </Card>
                </div>
            )}

            {loading ? (
                <div className="col-12 flex justify-content-center py-5">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                </div>
            ) : !dashboard ? (
                <div className="col-12">
                    <Card>
                        <p className="text-color-secondary text-center">{appT('No data available for this period')}</p>
                    </Card>
                </div>
            ) : (
                <>
                    {/* -- Summary Cards - Row 1 ---------- */}
                    {hasFilter && (
                        <div className="col-12">
                            <div className="text-xs text-color-secondary text-right mt-1">
                                <i className="pi pi-filter mr-1" />{appT('Filtered view')}
                                {driverFilter && <span> — {driverFilter}</span>}
                                {routeFilter && <span> — {routeFilter}</span>}
                            </div>
                        </div>
                    )}
                    <div className="col-6 md:col-3">
                        <Card className="h-full">
                            <div className="text-center">
                                <div className="text-color-secondary text-sm mb-1">Rutas activas</div>
                                <div className="text-3xl font-bold text-primary">{displaySummary.routes_active}</div>
                            </div>
                        </Card>
                    </div>
                    <div className="col-6 md:col-3">
                        <Card className="h-full">
                            <div className="text-center">
                                <div className="text-color-secondary text-sm mb-1">{appT('Machines')}</div>
                                <div className="text-3xl font-bold">
                                    {displaySummary.machines_serviced} / {displaySummary.machines_planned}
                                </div>
                            </div>
                        </Card>
                    </div>
                    <div className="col-6 md:col-3">
                        <Card className="h-full">
                            <div className="text-center">
                                <div className="text-color-secondary text-sm mb-1">{appT('Compliance')}</div>
                                <div className={`text-3xl font-bold ${displaySummary.compliance_pct >= 90 ? 'text-green-600' : displaySummary.compliance_pct >= 70 ? 'text-orange-500' : 'text-red-500'}`}>
                                    {formatPct(displaySummary.compliance_pct)}
                                    {summaryData.trends?.compliance === 'up' && <i className="pi pi-arrow-up text-green-500 text-sm ml-2" title={`+${summaryData.trends.compliance_delta}pp`} />}
                                    {summaryData.trends?.compliance === 'down' && <i className="pi pi-arrow-down text-red-500 text-sm ml-2" title={`${summaryData.trends.compliance_delta}pp`} />}
                                </div>
                            </div>
                        </Card>
                    </div>
                    <div className="col-6 md:col-3">
                        <Card className="h-full">
                            <div className="text-center">
                                <div className="text-color-secondary text-sm mb-1">{appT('Avg. Par Level')}</div>
                                <div className="text-3xl font-bold text-blue-600">
                                    {formatPct(displaySummary.avg_par_level)}
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* -- Summary Cards - Row 2 ---------- */}
                    <div className="col-6 md:col-3">
                        <Card className="h-full">
                            <div className="text-center">
                                <div className="text-color-secondary text-sm mb-1">Ventas del período</div>
                                <div className="text-3xl font-bold text-primary">{formatMoney(displaySummary.total_sold)}</div>
                                <div className="text-xs text-color-secondary mt-1">
                                    {appT('Cashless')}: {formatMoney(displaySummary.total_cashless)}
                                </div>
                            </div>
                        </Card>
                    </div>
                    <div className="col-6 md:col-3">
                        <Card className="h-full">
                            <div className="text-center">
                                <div className="text-color-secondary text-sm mb-1">{appT('Avg Hours/Route')}</div>
                                <div className="text-3xl font-bold">{displaySummary.avg_hours_per_route ?? '-'}</div>
                            </div>
                        </Card>
                    </div>
                    <div className="col-6 md:col-3">
                        <Card className="h-full">
                            <div className="text-center">
                                <div className="text-color-secondary text-sm mb-1">{appT('Unserviced')}</div>
                                <div className={`text-3xl font-bold ${(displaySummary.unserviced_count || 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                    {displaySummary.unserviced_count ?? 0}
                                    {summaryData.trends?.unserviced === 'improving' && <i className="pi pi-arrow-down text-green-500 text-sm ml-2" title={appT('Improving')} />}
                                    {summaryData.trends?.unserviced === 'worsening' && <i className="pi pi-arrow-up text-red-500 text-sm ml-2" title={appT('Worsening')} />}
                                </div>
                            </div>
                        </Card>
                    </div>
                    {showLiveAlerts && (
                    <div className="col-6 md:col-3">
                        <Card className="h-full">
                            <div className="text-center">
                                <div className="text-color-secondary text-sm mb-1">{appT('Not Visited (5+ days)')}</div>
                                <div className={`text-3xl font-bold ${overdueMachines.length > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                    {overdueMachines.length}
                                </div>
                            </div>
                        </Card>
                    </div>
                    )}
                    <div className="col-6 md:col-3">
                        <Card className="h-full">
                            <button type="button" className="p-link text-center w-full" onClick={() => setOverShortOpen(true)} disabled={loading || overShortRows === null} aria-label="Ver detalle de sobrante/faltante">
                                <div className="text-color-secondary text-sm mb-1">{appT('Over/Short')}</div>
                                <div className={`text-3xl font-bold ${displaySummary.total_over_short < 0 ? 'text-red-500' : 'text-green-500'}`}>
                                    {formatMoney(displaySummary.total_over_short)}
                                </div>
                                <div className="text-sm mt-2">{overShortRows === null ? 'Detalle no disponible' : 'Ver máquinas y fechas'} <i className="pi pi-angle-right" aria-hidden="true" /></div>
                            </button>
                            {overShortOpen && overShortRows !== null && <OverShortDialog
                                key={`${dashboard?.summary?.period}|${driverFilter}|${routeFilter}`}
                                rows={overShortRows} driver={driverFilter} route={routeFilter} period={dashboard?.summary?.period} excludedWithoutRoute={excludedWithoutRoute}
                                onHide={() => setOverShortOpen(false)}
                            />}
                        </Card>
                    </div>

                    {/* -- Comparison Table (side-by-side) -- */}
                    {variance && comparison && (
                        <div className="col-12">
                            <Card title={
                                <span><i className="pi pi-arrows-h mr-2" />{appT('vs Previous')}</span>
                            }>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: '0 4px' }}>
                                        <thead>
                                            <tr className="text-color-secondary">
                                                <th className="text-left py-2 px-3">{appT('Metric')}</th>
                                                <th className="text-right py-2 px-3">{summaryData.period}</th>
                                                <th className="text-right py-2 px-3">{comparison.period}</th>
                                                <th className="text-right py-2 px-3">{appT('Change')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                { label: 'Ventas del período', cur: summaryData.total_sold, prev: comparison.total_sold, delta: variance.sales_pct != null ? `${variancePrefix(variance.sales_pct)}%` : '-', pct: variance.sales_pct },
                                                { label: appT('Cashless'), cur: summaryData.total_cashless, prev: comparison.total_cashless, delta: variance.cashless_pct != null ? `${variancePrefix(variance.cashless_pct)}%` : '-', pct: variance.cashless_pct },
                                                { label: appT('Compliance'), cur: `${summaryData.compliance_pct}%`, prev: `${comparison.compliance_pct}%`, delta: `${variancePrefix(variance.compliance_pct)}pp`, pct: variance.compliance_pct },
                                                { label: appT('Avg Par Level'), cur: `${summaryData.avg_par_level}%`, prev: `${comparison.avg_par_level}%`, delta: `${variancePrefix(variance.par_level_pct)}pp`, pct: variance.par_level_pct },
                                                { label: appT('Machines'), cur: `${summaryData.machines_serviced}/${summaryData.machines_planned}`, prev: `${comparison.machines_serviced}/${comparison.machines_planned}`, delta: variance.machines_serviced_pct != null ? `${variancePrefix(variance.machines_serviced_pct)}%` : '-', pct: variance.machines_serviced_pct },
                                                { label: appT('Unserviced'), cur: summaryData.unserviced_count ?? 0, prev: comparison.unserviced_count ?? 0, delta: variance.unserviced_delta > 0 ? `+${variance.unserviced_delta}` : String(variance.unserviced_delta ?? 0), pct: -(variance.unserviced_delta || 0) },
                                                { label: appT('Avg Hours/Route'), cur: summaryData.avg_hours_per_route ?? '-', prev: comparison.avg_hours_per_route ?? '-', delta: variance.avg_hours_delta > 0 ? `+${variance.avg_hours_delta}h` : `${variance.avg_hours_delta ?? 0}h`, pct: -(variance.avg_hours_delta || 0) },
                                                { label: appT('Over/Short'), cur: summaryData.total_over_short, prev: comparison.total_over_short, delta: variance.over_short_delta ?? '-', pct: null },
                                            ].map((row, i) => (
                                                <tr key={i} className="surface-ground border-round">
                                                    <td className="py-2 px-3 font-semibold">{row.label}</td>
                                                    <td className="py-2 px-3 text-right font-bold">{row.cur}</td>
                                                    <td className="py-2 px-3 text-right text-color-secondary">{row.prev}</td>
                                                    <td className={`py-2 px-3 text-right font-bold ${row.pct != null ? varianceColor(row.pct) : ''}`}>{row.delta}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        </div>
                    )}

                    {/* -- Trend Analytics ────────────── */}
                    {isMultiDay && (
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => { toggleSection('trends'); if (!trendsData && !trendsLoading) fetchTrends(); }}>
                                    <span><i className="pi pi-chart-bar mr-2 text-blue-500" />{appT('Trend Analytics')}</span>
                                    <Button icon={expanded.trends ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" />
                                </div>
                            }>
                                {expanded.trends && (
                                    <>
                                        <div className="flex justify-content-end mb-3">
                                            <SelectButton
                                                value={granularity}
                                                options={granularityOptions}
                                                onChange={(e) => { if (e.value) { setGranularity(e.value); fetchTrends(e.value); } }}
                                            />
                                        </div>

                                        {trendsLoading ? (
                                            <div className="flex justify-content-center py-5"><ProgressSpinner style={{ width: '40px', height: '40px' }} /></div>
                                        ) : trendsData ? (
                                            <div className="grid">
                                                {salesChartData && (
                                                    <div className="col-12 md:col-6">
                                                        <h4 className="mt-0 mb-2 text-color-secondary">{appT('Sales Trend')}</h4>
                                                        <div style={{ height: '300px' }}><Chart type="line" data={salesChartData} options={moneyChartOptions} /></div>
                                                    </div>
                                                )}
                                                {overShortChartData && (
                                                    <div className="col-12 md:col-6">
                                                        <h4 className="mt-0 mb-2 text-color-secondary">{appT('Over/Short Trend')}</h4>
                                                        <div style={{ height: '300px' }}><Chart type="line" data={overShortChartData} options={osChartOptions} /></div>
                                                    </div>
                                                )}
                                                {complianceChartData && (
                                                    <div className="col-12 md:col-6">
                                                        <h4 className="mt-0 mb-2 text-color-secondary">{appT('Compliance %')}</h4>
                                                        <div style={{ height: '300px' }}><Chart type="line" data={complianceChartData} options={pctChartOptions} /></div>
                                                    </div>
                                                )}
                                                {parLevelChartData && (
                                                    <div className="col-12 md:col-6">
                                                        <h4 className="mt-0 mb-2 text-color-secondary">{appT('Par Level %')}</h4>
                                                        <div style={{ height: '300px' }}><Chart type="line" data={parLevelChartData} options={pctChartOptions} /></div>
                                                    </div>
                                                )}
                                                {machinesChartData && (
                                                    <div className="col-12 md:col-6">
                                                        <h4 className="mt-0 mb-2 text-color-secondary">{appT('Machines Serviced')}</h4>
                                                        <div style={{ height: '300px' }}><Chart type="bar" data={machinesChartData} options={countChartOptions} /></div>
                                                    </div>
                                                )}
                                                {driverChartData && (
                                                    <div className="col-12 md:col-6">
                                                        <h4 className="mt-0 mb-2 text-color-secondary">{appT('Driver Performance (Top 5)')}</h4>
                                                        <div style={{ height: '300px' }}><Chart type="line" data={driverChartData} options={moneyChartOptions} /></div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : null}
                                    </>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* -- Route Sales Trend ────────────── */}
                    {dashboard && (
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => { toggleSection('routeTrend'); if (routeNames.length === 0) fetchRouteNames(); }}>
                                    <span><i className="pi pi-chart-line mr-2 text-purple-500" />{appT('Route Sales Trend')}</span>
                                    <Button icon={expanded.routeTrend ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" />
                                </div>
                            }>
                                {expanded.routeTrend && (
                                    !isMultiDay ? (
                                        <p className="text-color-secondary text-center py-3">{appT('Select a date range greater than one day to see route sales trends')}</p>
                                    ) : (
                                    <>
                                        <div className="flex align-items-center gap-3 mb-3 flex-wrap">
                                            <Dropdown
                                                value={selectedRoute}
                                                options={[...new Set([...routeNames, routeFilter, selectedRoute].filter(Boolean))].map((r) => ({ label: r, value: r }))}
                                                onChange={(e) => { setSelectedRoute(e.value); fetchRouteTrend(e.value); }}
                                                placeholder={appT('Select Route')}
                                                filter
                                                showClear
                                                className="w-20rem"
                                            />
                                            <SelectButton
                                                value={routeGranularity}
                                                options={granularityOptions}
                                                onChange={(e) => { if (e.value) { setRouteGranularity(e.value); if (selectedRoute) fetchRouteTrend(selectedRoute, e.value); } }}
                                            />
                                            {routeTrendData?.totals && (
                                                <div className="flex gap-3 ml-auto text-sm">
                                                    <span className="text-color-secondary">{selectedRoute} — {appT('Route Sales')}: <strong>{formatMoney(routeTrendData.totals.pos_sales || routeTrendData.totals.consolidation_sold)}</strong></span>
                                                    <span className="text-color-secondary">{appT('Collected')}: <strong>{formatMoney(routeTrendData.totals.collected)}</strong></span>
                                                    <span className="text-color-secondary">{appT('Over/Short')}: <strong className={routeTrendData.totals.over_short >= 0 ? 'text-green-600' : 'text-red-500'}>{formatMoney(routeTrendData.totals.over_short)}</strong></span>
                                                </div>
                                            )}
                                        </div>

                                        {routeTrendLoading ? (
                                            <div className="flex justify-content-center py-5"><ProgressSpinner style={{ width: '40px', height: '40px' }} /></div>
                                        ) : routeTrendData ? (
                                            <div className="grid">
                                                {routeSalesChartData && (
                                                    <div className="col-12 md:col-6">
                                                        <h4 className="mt-0 mb-2 text-color-secondary">{appT('Sales Trend')}</h4>
                                                        <div style={{ height: '300px' }}><Chart type="line" data={routeSalesChartData} options={moneyChartOptions} /></div>
                                                    </div>
                                                )}
                                                {routeOsChartData && (
                                                    <div className="col-12 md:col-6">
                                                        <h4 className="mt-0 mb-2 text-color-secondary">{appT('Over/Short')}</h4>
                                                        <div style={{ height: '300px' }}><Chart type="line" data={routeOsChartData} options={osChartOptions} /></div>
                                                    </div>
                                                )}
                                                {routeMachinesChartData && (
                                                    <div className="col-12 md:col-6">
                                                        <h4 className="mt-0 mb-2 text-color-secondary">{appT('Machines Serviced')}</h4>
                                                        <div style={{ height: '300px' }}><Chart type="bar" data={routeMachinesChartData} options={countChartOptions} /></div>
                                                    </div>
                                                )}
                                                {routeParLevelChartData && (
                                                    <div className="col-12 md:col-6">
                                                        <h4 className="mt-0 mb-2 text-color-secondary">{appT('Par Level %')}</h4>
                                                        <div style={{ height: '300px' }}><Chart type="line" data={routeParLevelChartData} options={pctChartOptions} /></div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-color-secondary text-center py-3">{appT('Select a route to see its trend')}</p>
                                        )}
                                    </>
                                    )
                                )}
                            </Card>
                        </div>
                    )}

                    {/* -- Route Performance (Compliance + Efficiency merged) -- */}
                    {false && mergedRoutes.length > 0 && (
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('routes')}>
                                    <span><i className="pi pi-map mr-2" />{appT('Route Performance')} ({mergedRoutes.length})</span>
                                    <Button icon={expanded.routes ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('routes'); }} />
                                </div>
                            }>
                                {expanded.routes && (
                                    <DataTable value={mergedRoutes} paginator rows={15} emptyMessage={appT('No route data')} responsiveLayout="scroll" sortField="route_name" sortOrder={1} size="small">
                                        <Column field="route_name" header={appT('Route')} sortable style={{ minWidth: '120px', fontWeight: 'bold' }} />
                                        <Column field="driver" header={appT('Driver')} sortable style={{ minWidth: '130px' }} />
                                        <Column header={appT('Machines')} sortable sortField="actual_machines" body={(row) => `${row.actual_machines}/${row.planned_machines}`} style={{ width: '85px', textAlign: 'center' }} />
                                        <Column field="compliance_pct" header={appT('Compliance')} sortable body={(row) => <Tag value={formatPct(row.compliance_pct)} severity={complianceSeverity(row.compliance_pct)} />} style={{ width: '95px', textAlign: 'center' }} />
                                        <Column field="overall_score" header={appT('Score')} sortable body={(row) => {
                                            if (row.overall_score == null) return '-';
                                            const color = row.overall_score >= 85 ? 'text-green-600' : row.overall_score >= 70 ? 'text-blue-500' : row.overall_score >= 50 ? 'text-orange-500' : 'text-red-500';
                                            return <span className={`font-bold ${color}`}>{row.overall_score}</span>;
                                        }} style={{ width: '65px', textAlign: 'center' }} />
                                        <Column field="rating" header={appT('Rating')} sortable body={(row) => {
                                            if (!row.rating) return '-';
                                            const map = { 'Excellent': 'success', 'Good': 'info', 'Needs Improvement': 'warning', 'Critical': 'danger' };
                                            return <Tag value={appT(row.rating)} severity={map[row.rating] || 'info'} />;
                                        }} style={{ width: '120px', textAlign: 'center' }} />
                                        <Column field="avg_per_machine" header={appT('Avg/Machine')} sortable body={(row) => {
                                            if (row.avg_per_machine == null) return '-';
                                            const color = row.avg_per_machine > 16 ? 'text-red-500' : row.avg_per_machine > 8 ? 'text-orange-500' : 'text-green-600';
                                            return <span className={color}>{row.avg_per_machine} min</span>;
                                        }} style={{ width: '95px', textAlign: 'center' }} />
                                        <Column field="idle_pct" header={appT('Idle %')} sortable body={(row) => {
                                            if (row.idle_pct == null) return '-';
                                            const v = parseFloat(row.idle_pct);
                                            const color = v > 40 ? 'text-red-500' : v > 20 ? 'text-orange-500' : '';
                                            return <span className={color}>{formatPct(row.idle_pct)}</span>;
                                        }} style={{ width: '70px', textAlign: 'center' }} />
                                        <Column field="total_sold" header={appT('Sold')} sortable body={(row) => formatMoney(row.total_sold)} style={{ width: '90px', textAlign: 'right' }} />
                                        <Column field="over_short" header={appT('O/S')} sortable body={(row) => (<span className={row.over_short < 0 ? 'text-red-500 font-semibold' : row.over_short > 0 ? 'text-green-500' : ''}>{formatMoney(row.over_short)}</span>)} style={{ width: '90px', textAlign: 'right' }} />
                                        <Column field="sales_per_machine" header={appT('$/Machine')} sortable body={(row) => row.sales_per_machine != null ? formatMoney(row.sales_per_machine) : '-'} style={{ width: '90px', textAlign: 'right' }} />
                                    </DataTable>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* -- Compliance Alerts -------------- */}
                    {complianceAlerts.length > 0 && (
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('compAlerts')}>
                                    <span>
                                        <i className="pi pi-bell mr-2 text-red-500" />
                                        {appT('Compliance Alerts')} ({complianceAlerts.length})
                                        <Tag value={complianceAlerts.filter((a) => a.severity === 'critical').length || complianceAlerts.length} severity="danger" className="ml-2" />
                                    </span>
                                    <Button icon={expanded.compAlerts ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('compAlerts'); }} />
                                </div>
                            }>
                                {expanded.compAlerts && (
                                    <DataTable value={complianceAlerts} responsiveLayout="scroll" size="small" sortField="avg_compliance" sortOrder={1}>
                                        <Column header={appT('Type')} body={(row) => <Tag value={row.type === 'route' ? appT('Route') : appT('Driver')} severity="info" />} style={{ width: '80px' }} />
                                        <Column header={appT('Name')} body={(row) => row.type === 'route' ? row.route_name : row.driver} sortable sortField="route_name" style={{ minWidth: '140px', fontWeight: 'bold' }} />
                                        <Column header={appT('Driver')} body={(row) => row.driver || '-'} style={{ minWidth: '130px' }} />
                                        <Column header={appT('Days Below 90%')} body={(row) => (
                                            <span className="font-bold text-red-500">{row.days_below} / {row.total_days}</span>
                                        )} style={{ width: '120px', textAlign: 'center' }} />
                                        <Column header={appT('Avg Compliance')} sortable sortField="avg_compliance" body={(row) => (
                                            <Tag value={formatPct(row.avg_compliance)} severity={complianceSeverity(row.avg_compliance)} />
                                        )} style={{ width: '120px', textAlign: 'center' }} />
                                        <Column header={appT('Severity')} body={(row) => (
                                            <Tag value={row.severity === 'critical' ? appT('Critical') : appT('Warning')} severity={row.severity === 'critical' ? 'danger' : 'warning'} />
                                        )} style={{ width: '100px' }} />
                                        <Column header={appT('Daily Detail')} body={(row) => (
                                            <div className="flex flex-wrap gap-1">
                                                {row.details.map((d, i) => (
                                                    <Tag key={i} value={`${d.date.substring(0, 5)}: ${d.serviced}/${d.total}`} severity={complianceSeverity(d.compliance_pct)} className="text-xs" />
                                                ))}
                                            </div>
                                        )} style={{ minWidth: '200px' }} />
                                    </DataTable>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* -- Machines Not Visited (5+ Days) -- */}
                    {overdueMachines.length > 0 && (
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('overdueInventory')}>
                                    <span>
                                        <i className="pi pi-clock mr-2 text-red-500" />
                                        {appT('Machines Not Visited')} ({overdueMachines.length})
                                        <Tag value={overdueMachines.length} severity="danger" className="ml-2" />
                                    </span>
                                    <Button icon={expanded.overdueInventory ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('overdueInventory'); }} />
                                </div>
                            }>
                                {expanded.overdueInventory && (
                                    <DataTable value={overdueMachines} paginator rows={15} responsiveLayout="scroll" size="small" sortField="days_since_service" sortOrder={-1}>
                                        <Column field="machine_name" header={appT('Machine')} sortable style={{ minWidth: '180px' }} />
                                        <Column field="route" header={appT('Route')} sortable style={{ minWidth: '120px' }} />
                                        <Column field="location" header={appT('Location')} sortable style={{ minWidth: '160px' }} />
                                        <Column field="days_since_service" header={appT('Days Since Service')} sortable body={(row) => {
                                            const d = row.days_since_service;
                                            const severity = d >= 15 ? 'danger' : d >= 10 ? 'warning' : 'info';
                                            return <Tag value={`${d} ${appT('days')}`} severity={severity} />;
                                        }} style={{ width: '130px', textAlign: 'center' }} />
                                        <Column header={appT('Online')} body={(row) => (
                                            <i className={`pi pi-circle-fill ${row.is_offline ? 'text-red-400' : 'text-green-500'}`} style={{ fontSize: '0.75rem' }} title={row.is_offline ? 'Offline' : 'Online'} />
                                        )} style={{ width: '70px', textAlign: 'center' }} />
                                        <Column field="make_model" header={appT('Model')} style={{ minWidth: '120px' }} />
                                        <Column field="serial_number" header={appT('Serial')} style={{ minWidth: '100px' }} />
                                    </DataTable>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* -- Unserviced Machines (Selected Period) ------------ */}
                    <div className="col-12">
                        <Card title={
                            <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('unservicedPeriod')}>
                                <span>
                                    <i className="pi pi-exclamation-circle mr-2 text-red-500" />
                                    Máquinas no atendidas ({unserviced.length})
                                    {unserviced.length > 0 && <Tag value={unserviced.length} severity="danger" className="ml-2" />}
                                </span>
                                <Button icon={expanded.unservicedPeriod ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('unservicedPeriod'); }} />
                            </div>
                        }>
                            {expanded.unservicedPeriod && (
                                unserviced.length > 0 ? (
                                    <DataTable value={unserviced} paginator rows={20} responsiveLayout="scroll" size="small" sortField="route_name" sortOrder={1}>
                                        <Column field="route_name" header={appT('Route')} sortable style={{ minWidth: '120px' }} />
                                        <Column field="driver" header={appT('Driver')} sortable style={{ minWidth: '120px' }} />
                                        <Column field="location_name" header={appT('Location')} sortable style={{ minWidth: '160px' }} />
                                        <Column field="machine_name" header={appT('Machine')} sortable style={{ minWidth: '150px' }} />
                                        <Column field="machine_type" header={appT('Type')} sortable style={{ width: '90px' }} />
                                        <Column field="parlevel_pct" header="PL %" sortable body={(row) => row.parlevel_pct || '-'} style={{ width: '80px' }} />
                                        <Column field="estimated_lost_revenue" header="Pérdida est." sortable body={(row) => formatMoney(row.estimated_lost_revenue || 0)} style={{ width: '120px', textAlign: 'right' }} />
                                    </DataTable>
                                ) : (
                                    <p className="text-color-secondary text-center m-0">{appT('All machines serviced')}</p>
                                )
                            )}
                        </Card>
                    </div>

                    {/* -- Unserviced Machines (15-day extended list) ------------ */}
                    <div className="col-12">
                        <Card title={
                            <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('unserviced')}>
                                <span>
                                    <i className="pi pi-exclamation-triangle mr-2 text-orange-500" />
                                    Máquinas no agendadas o visitadas en los últimos 15 días ({longWindowUnserviced.length})
                                    {longWindowUnserviced.length > 0 && <Tag value={longWindowUnserviced.length} severity="danger" className="ml-2" />}
                                </span>
                                <Button icon={expanded.unserviced ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('unserviced'); }} />
                            </div>
                        }>
                            {expanded.unserviced && (
                                longWindowUnserviced.length > 0 ? (
                                    <DataTable value={longWindowUnserviced} paginator rows={20} responsiveLayout="scroll" size="small" sortField="days_since_service" sortOrder={-1}>
                                        <Column field="route_name" header={appT('Route')} sortable style={{ minWidth: '120px' }} />
                                        <Column field="location_name" header={appT('Location')} sortable style={{ minWidth: '160px' }} />
                                        <Column field="machine_name" header={appT('Machine')} sortable style={{ minWidth: '150px' }} />
                                        <Column field="machine_type" header={appT('Type')} sortable style={{ width: '80px' }} />
                                        <Column field="issue_15d" header="Estado 15d" sortable body={(row) => (
                                            <Tag value={row.issue_15d || '-'} severity={row.issue_15d === 'No agendada' ? 'danger' : 'warning'} />
                                        )} style={{ width: '120px', textAlign: 'center' }} />
                                        <Column field="last_planned_date_15d" header="Últ. agendada" sortable body={(row) => row.last_planned_date_15d || '-'} style={{ width: '110px', textAlign: 'center' }} />
                                        <Column field="days_since_service" header="Días sin visita" sortable body={(row) => row.days_since_service ?? '-'} style={{ width: '110px', textAlign: 'center' }} />
                                    </DataTable>
                                ) : (
                                    <p className="text-color-secondary text-center m-0">Sin máquinas pendientes en ventana de 15 días</p>
                                )
                            )}
                        </Card>
                    </div>

                    {/* -- Machine Detail ----------------- */}
                    {machineDetail.length > 0 && (
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('machineDetail')}>
                                    <span><i className="pi pi-list mr-2" />{appT('Machine Detail')} ({machineDetail.length})</span>
                                    <Button icon={expanded.machineDetail ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('machineDetail'); }} />
                                </div>
                            }>
                                {expanded.machineDetail && (
                                    <DataTable value={machineDetail} paginator rows={25} rowsPerPageOptions={[25, 50, 100]} emptyMessage={appT('No machine data')} responsiveLayout="scroll" sortField="route_name" sortOrder={1} size="small" filterDisplay="row">
                                        <Column field="route_name" header={appT('Route')} sortable filter filterPlaceholder="..." style={{ minWidth: '120px' }} />
                                        <Column field="location_name" header={appT('Location')} sortable style={{ minWidth: '160px' }} />
                                        <Column field="machine_name" header={appT('Machine')} sortable style={{ minWidth: '150px' }} />
                                        <Column field="machine_type" header={appT('Type')} sortable style={{ width: '80px' }} />
                                        <Column field="parlevel_pct" header="PL %" sortable body={(row) => {
                                            if (!row.parlevel_pct || row.parlevel_pct === '-') return '-';
                                            const num = parseFloat(row.parlevel_pct);
                                            const severity = num >= 90 ? 'success' : num >= 70 ? 'warning' : 'danger';
                                            return <Tag value={row.parlevel_pct} severity={severity} />;
                                        }} style={{ width: '90px' }} />
                                        <Column field="sold" header={appT('Sale')} sortable body={(row) => row.sold != null && row.sold !== 0 ? formatMoney(row.sold) : '-'} style={{ width: '90px', textAlign: 'right' }} />
                                        <Column field="service_time" header={appT('Time')} sortable body={(row) => formatDisplayTime(row.service_time)} style={{ width: '85px' }} />
                                        <Column field="gap" header={appT('Gap')} body={(row) => row.gap || '-'} style={{ width: '70px' }} />
                                        <Column field="status" header={appT('Status')} sortable body={(row) => {
                                            if (row.status === 'Consolidated') return <i className="pi pi-check-circle text-green-500" />;
                                            if (row.status === 'Pending') return <i className="pi pi-clock text-orange-500" />;
                                            return <span className="text-sm">{row.status}</span>;
                                        }} style={{ width: '70px', textAlign: 'center' }} />
                                    </DataTable>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* -- Driver Performance ------------- */}
                    {driverPerf.length > 0 && (
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('drivers')}>
                                    <span><i className="pi pi-users mr-2" />{appT('Driver Performance')} ({driverPerf.length})</span>
                                    <Button icon={expanded.drivers ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('drivers'); }} />
                                </div>
                            }>
                                {expanded.drivers && (
                                    <>
                                        <DataTable
                                            value={driverPerf}
                                            emptyMessage={appT('No driver data')}
                                            responsiveLayout="scroll"
                                            sortField="driver_name"
                                            sortOrder={1}
                                            size="small"
                                            footerColumnGroup={driverPerf.length > 1 ? (
                                                <ColumnGroup>
                                                    <Row>
                                                        <Column footer="TOTAL" style={{ fontWeight: 'bold' }} />
                                                        <Column footer={`${driverPerf.reduce((s, d) => s + d.machines_serviced, 0)}/${driverPerf.reduce((s, d) => s + d.machines_planned, 0)}`} style={{ fontWeight: 'bold', textAlign: 'center' }} />
                                                        <Column footer={(() => { const s = driverPerf.reduce((a, d) => a + d.machines_serviced, 0); const p = driverPerf.reduce((a, d) => a + d.machines_planned, 0); return p > 0 ? formatPct(((s / p) * 100).toFixed(1)) : '0%'; })()} style={{ fontWeight: 'bold', textAlign: 'center' }} />
                                                        <Column footer={(() => { const pls = driverPerf.filter((d) => d.avg_par_level > 0); return pls.length > 0 ? formatPct((pls.reduce((a, d) => a + d.avg_par_level, 0) / pls.length).toFixed(1)) : '-'; })()} style={{ fontWeight: 'bold', textAlign: 'center' }} />
                                                        <Column footer="" />{/* routes */}
                                                        {isMultiDay ? <Column footer="" /> : <Column footer="" />}{/* days | hora inicial */}
                                                        {isMultiDay ? <Column footer="" /> : <Column footer="" />}{/* avg duration | hora final */}
                                                        {isMultiDay ? <Column footer="" /> : null}{/* avg duration */}
                                                        <Column footer={driverPerf.reduce((s, d) => s + (d.gap_count || 0), 0)} style={{ fontWeight: 'bold', textAlign: 'center' }} />
                                                        <Column footer={driverPerf.reduce((s, d) => s + (d.after_hours_count || 0), 0)} style={{ fontWeight: 'bold', textAlign: 'center' }} />
                                                        {isMultiDay ? <Column footer={formatMoney(driverPerf.reduce((s, d) => s + (d._raw_sold || parseFloat(d.total_sold) || 0), 0))} style={{ fontWeight: 'bold', textAlign: 'right' }} /> : null}
                                                        {isMultiDay ? <Column footer={formatMoney(driverPerf.reduce((s, d) => s + (typeof d.total_over_short === 'number' ? d.total_over_short : parseFloat(d.total_over_short) || 0), 0))} style={{ fontWeight: 'bold', textAlign: 'right' }} /> : null}
                                                    </Row>
                                                </ColumnGroup>
                                            ) : undefined}
                                        >
                                            <Column field="driver_name" header={appT('Driver')} sortable style={{ minWidth: '150px', fontWeight: 'bold' }} />
                                            <Column header={appT('Serviced/Planned')} sortable sortField="machines_serviced" body={(row) => `${row.machines_serviced}/${row.machines_planned}`} style={{ width: '110px', textAlign: 'center' }} />
                                            <Column field="compliance_pct" header={appT('Compliance %')} sortable body={(row) => {
                                                const v = row.compliance_pct;
                                                const severity = complianceSeverity(v);
                                                return <Tag value={formatPct(v)} severity={severity} />;
                                            }} style={{ width: '100px', textAlign: 'center' }} />
                                            <Column field="avg_par_level" header={appT('Fill %')} sortable body={(row) => {
                                                if (!row.avg_par_level) return '-';
                                                return <span>{formatPct(row.avg_par_level)}</span>;
                                            }} style={{ width: '80px', textAlign: 'center' }} />
                                            <Column field="routes" header={appT('Routes')} body={(row) => <span className="text-sm">{row.routes || '-'}</span>} style={{ minWidth: '120px' }} />
                                            {isMultiDay && (
                                                <Column field="days_active" header={appT('Days')} sortable style={{ width: '60px', textAlign: 'center' }} />
                                            )}
                                            <Column field="first_service_time" header="Hora inicial" sortable sortField="first_service_min" body={(row) => formatDisplayTime(row.first_service_time)} style={{ width: '105px', textAlign: 'center' }} />
                                            <Column field="last_service_time" header="Hora final" sortable sortField="last_service_min" body={(row) => formatDisplayTime(row.last_service_time)} style={{ width: '105px', textAlign: 'center' }} />
                                            {isMultiDay ? (
                                                <Column field="avg_duration" header={appT('Avg Duration')} sortable sortField="avg_duration_min" style={{ width: '95px', textAlign: 'center' }} />
                                            ) : (
                                                <Column field="avg_duration" header="Duración" sortable sortField="avg_duration_min" style={{ width: '95px', textAlign: 'center' }} />
                                            )}
                                            <Column field="gap_count" header={appT('Gaps')} sortable body={(row) => (
                                                <span className={row.gap_count > 5 ? 'text-orange-500 font-semibold' : ''}>{row.gap_count || '-'}</span>
                                            )} style={{ width: '60px', textAlign: 'center' }} />
                                            <Column field="after_hours_count" header={appT('After Hrs')} sortable body={(row) => (
                                                <span className={row.after_hours_count > 0 ? 'text-red-500 font-semibold' : ''}>{row.after_hours_count || '-'}</span>
                                            )} style={{ width: '75px', textAlign: 'center' }} />
                                            {isMultiDay ? <Column field="total_sold" header={appT('Sales')} sortable body={(row) => formatMoney(row.total_sold)} style={{ width: '100px', textAlign: 'right' }} /> : null}
                                            {isMultiDay ? <Column field="total_over_short" header={appT('O/S')} sortable body={(row) => (<span className={parseFloat(row.total_over_short) < 0 ? 'text-red-500 font-semibold' : parseFloat(row.total_over_short) > 0 ? 'text-green-500' : ''}>{formatMoney(row.total_over_short)}</span>)} style={{ width: '90px', textAlign: 'right' }} /> : null}
                                        </DataTable>
                                    </>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* Route Efficiency Ranking — merged into Route Performance above */}

                    {/* -- Machine Type Summary ------------- */}
                    {false && (dashboard?.machine_type_summary?.length > 0) && (
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('machineTypes')}>
                                    <span><i className="pi pi-box mr-2 text-teal-500" />{appT('Sales by Machine Type')}</span>
                                    <Button icon={expanded.machineTypes ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('machineTypes'); }} />
                                </div>
                            }>
                                {expanded.machineTypes && (
                                    <DataTable value={dashboard.machine_type_summary} size="small" responsiveLayout="scroll" sortField="_raw_sold" sortOrder={-1}>
                                        <Column field="type" header={appT('Machine Type')} sortable style={{ minWidth: '160px', fontWeight: 'bold' }} />
                                        <Column field="unique_machines" header={appT('Machines')} sortable style={{ width: '90px', textAlign: 'center' }} />
                                        <Column field="total_services" header={appT('Services')} sortable style={{ width: '90px', textAlign: 'center' }} />
                                        <Column field="avg_par_level" header={appT('Avg Par %')} sortable body={(row) => {
                                            if (!row.avg_par_level) return '-';
                                            const color = row.avg_par_level >= 80 ? 'text-green-600' : row.avg_par_level >= 60 ? 'text-orange-500' : 'text-red-500';
                                            return <span className={color}>{formatPct(row.avg_par_level)}</span>;
                                        }} style={{ width: '90px', textAlign: 'center' }} />
                                        <Column field="total_sold" header={appT('Total Sales')} sortable body={(row) => formatMoney(row.total_sold)} style={{ width: '110px', textAlign: 'right' }} />
                                    </DataTable>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* -- Route Optimization Report ------ */}
                    {false && routeOptRows.length > 0 && (
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('routeOpt')}>
                                    <span><i className="pi pi-truck mr-2 text-blue-500" />{appT('Route Optimization Report')}</span>
                                    <Button icon={expanded.routeOpt ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('routeOpt'); }} />
                                </div>
                            }>
                                {expanded.routeOpt && (
                                    <>
                                        {/* Efficiency Summary KPIs */}
                                        <div className="grid mb-3">
                                            <div className="col-6 md:col-2">
                                                <div className="surface-100 border-round p-3 text-center">
                                                    <div className="text-sm text-color-secondary">{appT('Route-Days')}</div>
                                                    <div className="text-2xl font-bold">{routeOptSummary.total_route_days}</div>
                                                </div>
                                            </div>
                                            <div className="col-6 md:col-2">
                                                <div className="surface-100 border-round p-3 text-center">
                                                    <div className="text-sm text-color-secondary">{appT('Machines')}</div>
                                                    <div className="text-2xl font-bold">{routeOptSummary.total_machines_serviced}</div>
                                                </div>
                                            </div>
                                            <div className="col-6 md:col-2">
                                                <div className="surface-100 border-round p-3 text-center">
                                                    <div className="text-sm text-color-secondary">{appT('Avg Time/Machine')}</div>
                                                    <div className={`text-2xl font-bold ${routeOptSummary.avg_time_per_machine > routeOptSummary.target_time * 2 ? 'text-red-500' : routeOptSummary.avg_time_per_machine > routeOptSummary.target_time ? 'text-orange-500' : 'text-green-600'}`}>
                                                        {routeOptSummary.avg_time_per_machine} min
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6 md:col-2">
                                                <div className="surface-100 border-round p-3 text-center">
                                                    <div className="text-sm text-color-secondary">{appT('Target')}</div>
                                                    <div className="text-2xl font-bold text-blue-500">{routeOptSummary.target_time} min</div>
                                                </div>
                                            </div>
                                            <div className="col-12 md:col-4">
                                                <div className="surface-100 border-round p-3">
                                                    <div className="flex justify-content-around align-items-center gap-2 flex-wrap">
                                                        <span className="cursor-pointer" onClick={() => setEfficiencyFilter(efficiencyFilter === 'Efficient' ? null : 'Efficient')}>
                                                            <i className="pi pi-check-circle text-green-500 mr-1" />
                                                            <span className={`font-semibold ${efficiencyFilter === 'Efficient' ? 'underline' : ''}`}>{appT('Efficient')}</span>
                                                            <span className="ml-1 font-bold">{routeOptSummary.efficient_count}</span>
                                                        </span>
                                                        <span className="cursor-pointer" onClick={() => setEfficiencyFilter(efficiencyFilter === 'Average' ? null : 'Average')}>
                                                            <i className="pi pi-circle-fill text-orange-400 mr-1" style={{ fontSize: '0.7rem' }} />
                                                            <span className={`font-semibold ${efficiencyFilter === 'Average' ? 'underline' : ''}`}>{appT('Average')}</span>
                                                            <span className="ml-1 font-bold">{routeOptSummary.average_count}</span>
                                                        </span>
                                                        <span className="cursor-pointer" onClick={() => setEfficiencyFilter(efficiencyFilter === 'Slow' ? null : 'Slow')}>
                                                            <i className="pi pi-circle-fill text-red-500 mr-1" style={{ fontSize: '0.7rem' }} />
                                                            <span className={`font-semibold ${efficiencyFilter === 'Slow' ? 'underline' : ''}`}>{appT('Slow')}</span>
                                                            <span className="ml-1 font-bold">{routeOptSummary.slow_count}</span>
                                                        </span>
                                                        <span className="cursor-pointer" onClick={() => setEfficiencyFilter(efficiencyFilter === 'Too Fast' ? null : 'Too Fast')}>
                                                            <i className="pi pi-circle-fill text-yellow-400 mr-1" style={{ fontSize: '0.7rem' }} />
                                                            <span className={`font-semibold ${efficiencyFilter === 'Too Fast' ? 'underline' : ''}`}>{appT('Too Fast')}</span>
                                                            <span className="ml-1 font-bold">{routeOptSummary.too_fast_count}</span>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {efficiencyFilter && (
                                            <div className="flex align-items-center gap-2 mb-2">
                                                <Tag value={`${appT('Filtered')}: ${efficiencyFilter}`} severity="info" />
                                                <Button label={appT('Clear')} className="p-button-text p-button-sm" icon="pi pi-times" onClick={() => setEfficiencyFilter(null)} />
                                            </div>
                                        )}

                                        {/* Route Timing Details */}
                                        <DataTable
                                            value={efficiencyFilter
                                                ? routeOptRows.filter((r) => r.is_group ? r.route_name && routeOptRows.some((d) => !d.is_group && d.route_name === r.route_name && d.efficiency === efficiencyFilter) : r.efficiency === efficiencyFilter)
                                                : routeOptRows
                                            }
                                            emptyMessage={appT('No route timing data')}
                                            responsiveLayout="scroll"
                                            size="small"
                                            rowClassName={(row) => row.is_group ? 'surface-100 font-bold' : ''}
                                        >
                                            <Column field="route_name" header={appT('Route')} body={(row) => (
                                                <span style={{ paddingLeft: row.is_group ? 0 : '1.5rem', fontWeight: row.is_group ? 'bold' : 'normal' }}>
                                                    {row.is_group ? row.route_name : '\u2514\u2500'}
                                                </span>
                                            )} style={{ minWidth: '140px' }} />
                                            <Column field="service_date" header={appT('Date')} body={(row) => row.is_group ? '' : row.service_date} style={{ width: '100px' }} />
                                            <Column field="driver" header={appT('Driver')} body={(row) => row.is_group ? '' : row.driver} style={{ minWidth: '140px' }} />
                                            <Column field="machines" header={appT('Machines')} body={(row) => row.machines} style={{ width: '80px', textAlign: 'center' }} />
                                            <Column field="duration_display" header={appT('Duration')} body={(row) => row.is_group ? '' : row.duration_display} style={{ width: '90px' }} />
                                            <Column field="avg_per_machine" header={appT('Avg/Machine')} body={(row) => {
                                                const v = row.avg_per_machine;
                                                if (!v) return '-';
                                                const color = v > 16 ? 'text-red-500' : v > 8 ? 'text-orange-500' : 'text-green-600';
                                                return <span className={`font-semibold ${color}`}>{v} min</span>;
                                            }} style={{ width: '100px', textAlign: 'center' }} />
                                            <Column field="gap_count" header={appT('Gaps')} body={(row) => row.gap_count || '-'} style={{ width: '60px', textAlign: 'center' }} />
                                            <Column field="idle_pct" header={appT('Idle %')} body={(row) => {
                                                if (row.is_group) return row.idle_pct ? formatPct(row.idle_pct) : '-';
                                                if (!row.idle_pct) return '-';
                                                const v = parseFloat(row.idle_pct);
                                                const color = v > 40 ? 'text-red-500' : v > 20 ? 'text-orange-500' : '';
                                                return <span className={color}>{formatPct(row.idle_pct)}</span>;
                                            }} style={{ width: '70px', textAlign: 'center' }} />
                                            <Column field="efficiency" header={appT('Efficiency')} body={(row) => {
                                                if (row.is_group) return '';
                                                const eff = row.efficiency;
                                                const map = { 'Efficient': 'success', 'Average': 'warning', 'Slow': 'danger', 'Too Fast': 'info' };
                                                return <Tag value={appT(eff)} severity={map[eff] || 'info'} />;
                                            }} style={{ width: '100px', textAlign: 'center' }} />
                                        </DataTable>
                                    </>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* -- Route Optimization Insights ------ */}
                    {false && ((insights.late_routes?.length > 0) || (insights.gap_drivers?.length > 0) || (insights.repeat_unserviced?.length > 0)) && (
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('insights')}>
                                    <span><i className="pi pi-lightbulb mr-2 text-yellow-500" />{appT('Optimization Insights')}</span>
                                    <Button icon={expanded.insights ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('insights'); }} />
                                </div>
                            }>
                                {expanded.insights && (
                                    <div className="grid">
                                        {/* Late Routes */}
                                        {insights.late_routes?.length > 0 && (
                                            <div className="col-12">
                                                <h4 className="mt-0 mb-2 text-color-secondary">
                                                    <i className="pi pi-moon mr-2 text-orange-500" />{appT('Routes Running Late')}
                                                </h4>
                                                <DataTable value={insights.late_routes} size="small" responsiveLayout="scroll" emptyMessage="-" sortField="late_pct" sortOrder={-1}>
                                                    <Column field="route_name" header={appT('Route')} style={{ minWidth: '140px' }} />
                                                    <Column field="driver" header={appT('Driver')} style={{ minWidth: '120px' }} />
                                                    <Column field="days_late" header={appT('Days Late')} body={(row) => (
                                                        <span className={row.late_pct >= 50 ? 'text-red-500 font-bold' : row.late_pct >= 25 ? 'text-orange-500 font-semibold' : ''}>
                                                            {row.days_late}/{row.total_days}
                                                        </span>
                                                    )} style={{ width: '90px', textAlign: 'center' }} />
                                                    <Column field="late_pct" header="%" body={(row) => (
                                                        <Tag value={`${row.late_pct}%`} severity={row.late_pct >= 50 ? 'danger' : row.late_pct >= 25 ? 'warning' : 'info'} />
                                                    )} style={{ width: '80px', textAlign: 'center' }} />
                                                    <Column field="machines_after_hours" header={appT('Machines')} style={{ width: '90px', textAlign: 'center' }} />
                                                    <Column field="latest_time" header={appT('Latest')} style={{ width: '80px', textAlign: 'center' }} />
                                                </DataTable>
                                            </div>
                                        )}

                                        {/* Driver Idle Time */}
                                        {insights.gap_drivers?.length > 0 && (
                                            <div className="col-12 mt-3">
                                                <h4 className="mt-0 mb-2 text-color-secondary">
                                                    <i className="pi pi-pause-circle mr-2 text-blue-500" />{appT('Drivers with Most Idle Time')}
                                                </h4>
                                                <DataTable value={insights.gap_drivers} size="small" responsiveLayout="scroll" emptyMessage="-" sortField="idle_pct" sortOrder={-1}>
                                                    <Column field="driver" header={appT('Driver')} style={{ minWidth: '140px' }} />
                                                    <Column field="routes" header={appT('Routes')} style={{ minWidth: '140px' }} />
                                                    <Column field="route_days" header={appT('Route-Days')} style={{ width: '90px', textAlign: 'center' }} />
                                                    <Column field="total_gaps" header={appT('Total Gaps')} style={{ width: '90px', textAlign: 'center' }} />
                                                    <Column field="avg_gaps_per_day" header={appT('Gaps/Day')} style={{ width: '80px', textAlign: 'center' }} />
                                                    <Column field="idle_time" header={appT('Idle Time')} body={(row) => (
                                                        <span className={row.idle_pct >= 40 ? 'text-red-500 font-bold' : row.idle_pct >= 20 ? 'text-orange-500' : ''}>
                                                            {row.idle_time}
                                                        </span>
                                                    )} style={{ width: '90px', textAlign: 'center' }} />
                                                    <Column field="idle_pct" header={appT('Idle %')} body={(row) => (
                                                        <Tag value={`${row.idle_pct}%`} severity={row.idle_pct >= 40 ? 'danger' : row.idle_pct >= 20 ? 'warning' : 'success'} />
                                                    )} style={{ width: '80px', textAlign: 'center' }} />
                                                </DataTable>
                                            </div>
                                        )}

                                        {/* Frequently Unserviced Machines */}
                                        {insights.repeat_unserviced?.length > 0 && (
                                            <div className="col-12 mt-3">
                                                <h4 className="mt-0 mb-2 text-color-secondary">
                                                    <i className="pi pi-exclamation-triangle mr-2 text-red-500" />{appT('Frequently Unserviced Machines')}
                                                </h4>
                                                <DataTable value={insights.repeat_unserviced} size="small" responsiveLayout="scroll" emptyMessage="-" sortField="times_unserviced" sortOrder={-1} paginator={insights.repeat_unserviced.length > 15} rows={15}>
                                                    <Column field="route_name" header={appT('Route')} style={{ minWidth: '140px' }} />
                                                    <Column field="machine_name" header={appT('Machine')} style={{ minWidth: '160px' }} />
                                                    <Column field="location_name" header={appT('Location')} style={{ minWidth: '160px' }} />
                                                    <Column field="times_unserviced" header={appT('Times Missed')} body={(row) => (
                                                        <Tag value={row.times_unserviced} severity={row.times_unserviced >= 5 ? 'danger' : row.times_unserviced >= 3 ? 'warning' : 'info'} />
                                                    )} style={{ width: '100px', textAlign: 'center' }} />
                                                    <Column field="dates" header={appT('Dates')} style={{ minWidth: '200px' }} />
                                                </DataTable>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* -- Schedule Anomalies (Time Gaps + After Hours merged) -- */}
                    <div className="col-12">
                        <Card title={
                            <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('scheduleAnomalies')}>
                                <span>
                                    <i className="pi pi-exclamation-circle mr-2 text-orange-500" />
                                    {appT('Schedule Anomalies')}
                                    {timeGaps.length > 0 && <Tag value={`${timeGaps.length} ${appT('gaps')}`} severity="warning" className="ml-2" />}
                                    {afterHours.length > 0 && <Tag value={`${afterHours.length} ${appT('after hrs')}`} severity="danger" className="ml-2" />}
                                </span>
                                <Button icon={expanded.scheduleAnomalies ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('scheduleAnomalies'); }} />
                            </div>
                        }>
                            {expanded.scheduleAnomalies && (
                                scheduleAnomalies.length > 0 ? (
                                    <DataTable value={scheduleAnomalies} paginator={scheduleAnomalies.length > 15} rows={15} responsiveLayout="scroll" size="small" sortField="route_name" sortOrder={1}>
                                        <Column field="_type" header={appT('Type')} sortable body={(row) => (
                                            <Tag value={row._type === 'Gap' ? appT('Gap') : appT('After Hours')} severity={row._type === 'Gap' ? 'warning' : 'danger'} />
                                        )} style={{ width: '100px' }} />
                                        <Column field="route_name" header={appT('Route')} sortable style={{ minWidth: '110px' }} />
                                        <Column field="driver" header={appT('Driver')} sortable style={{ minWidth: '120px' }} />
                                        <Column field="service_date" header={appT('Date')} sortable style={{ width: '100px' }} />
                                        <Column field="time_display" header={appT('Time')} style={{ width: '110px' }} />
                                        <Column field="detail" header={appT('Detail')} style={{ minWidth: '180px' }} />
                                        <Column field="duration" header={appT('Duration')} body={(row) => row.duration !== '-' ? <Tag value={row.duration} severity="warning" /> : '-'} style={{ width: '90px', textAlign: 'center' }} />
                                        <Column field="severity_label" header={appT('Severity')} sortable body={(row) => (
                                            <Tag value={row.severity_label} severity={row.severity} />
                                        )} style={{ width: '110px', textAlign: 'center' }} />
                                    </DataTable>
                                ) : (
                                    <p className="text-color-secondary text-center m-0">{appT('No schedule anomalies detected')}</p>
                                )
                            )}
                        </Card>
                    </div>

                    {/* -- Route Sales Drops ≥15% --------------- */}
                    {(routeSalesDrops.length > 0 || hasRecentConsolidationLag) && (
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('routeDrops')}>
                                    <span>
                                        <i className="pi pi-chart-line mr-2 text-red-500" style={{ transform: 'scaleY(-1)' }} />
                                        {appT('Routes with Sales Drop')} ({'\u2265'}15%)
                                        <Tag value={routeSalesDrops.length} severity="danger" className="ml-2" />
                                    </span>
                                    <Button icon={expanded.routeDrops ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('routeDrops'); }} />
                                </div>
                            }>
                                {expanded.routeDrops && (
                                    <>
                                        {salesVariationPeriod.previous && (
                                            <div className="text-sm text-color-secondary mb-2">
                                                {appT('Comparing')}: <strong>{salesVariationPeriod.current}</strong> vs <strong>{salesVariationPeriod.previous}</strong>
                                            </div>
                                        )}
                                        {hasRecentConsolidationLag && (
                                            <div className="p-2 mb-2 border-round surface-100 text-sm">
                                                <i className="pi pi-info-circle mr-2 text-orange-500" />
                                                {preferredLanguage === 'es'
                                                    ? `Algunos datos de consolidado aún se están procesando para fechas recientes (${missingConsolidationDates.join(', ')}). Los valores pueden cambiar cuando termine la carga.`
                                                    : `Some consolidation data is still processing for recent dates (${missingConsolidationDates.join(', ')}). Values may change after ingestion completes.`}
                                            </div>
                                        )}
                                        {routeSalesDrops.length > 0 ? (
                                            <DataTable value={routeSalesDrops} emptyMessage={appT('No significant drops')} responsiveLayout="scroll" size="small" sortField="change_pct" sortOrder={1}>
                                                <Column field="route_name" header={appT('Route')} sortable style={{ minWidth: '140px', fontWeight: 'bold' }} />
                                                <Column field="driver" header={appT('Driver')} sortable style={{ minWidth: '150px' }} />
                                                <Column field="current_sold" header={appT('Current Sales')} sortable body={(row) => formatMoney(row.current_sold)} style={{ width: '130px', textAlign: 'right' }} />
                                                <Column field="previous_sold" header={appT('Previous Sales')} sortable body={(row) => formatMoney(row.previous_sold)} style={{ width: '130px', textAlign: 'right' }} />
                                                <Column field="change_pct" header={appT('Change')} sortable body={(row) => (
                                                    <span className="text-red-500 font-bold">{row.change_pct}%</span>
                                                )} style={{ width: '100px', textAlign: 'right' }} />
                                            </DataTable>
                                        ) : (
                                            <p className="text-color-secondary text-center m-0">{appT('No significant drops')}</p>
                                        )}
                                    </>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* -- Sales Variation vs Previous Period ---- */}
                    {salesVariation.length > 0 && (
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('salesVariation')}>
                                    <span>
                                        <i className="pi pi-dollar mr-2 text-orange-500" />
                                        {appT('Sales Variation vs Previous Period')} ({'\u2265'}10%)
                                        <Tag value={salesVariation.length} severity="warning" className="ml-2" />
                                    </span>
                                    <Button icon={expanded.salesVariation ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('salesVariation'); }} />
                                </div>
                            }>
                                {expanded.salesVariation && (
                                    <>
                                        {salesVariationPeriod.previous && (
                                            <div className="text-sm text-color-secondary mb-2">
                                                {appT('Comparing')}: <strong>{salesVariationPeriod.current}</strong> vs <strong>{salesVariationPeriod.previous}</strong>
                                            </div>
                                        )}
                                        <DataTable value={salesVariation} paginator rows={20} rowsPerPageOptions={[20, 50, 100]} emptyMessage={appT('No significant variation')} responsiveLayout="scroll" size="small" sortField="delta_amount" sortOrder={1} filterDisplay="row">
                                            <Column field="route_name" header={appT('Route')} sortable filter filterPlaceholder="..." style={{ minWidth: '120px' }} />
                                            <Column field="location_name" header={appT('Location')} sortable style={{ minWidth: '160px' }} />
                                            <Column field="machine_name" header={appT('Machine')} sortable style={{ minWidth: '140px' }} />
                                            <Column field="machine_type" header={appT('Type')} sortable style={{ width: '80px' }} />
                                            <Column field="current_sold" header={appT('Current Sales')} sortable body={(row) => formatMoney(row.current_sold)} style={{ width: '110px', textAlign: 'right' }} />
                                            <Column field="previous_sold" header={appT('Previous Sales')} sortable body={(row) => formatMoney(row.previous_sold)} style={{ width: '110px', textAlign: 'right' }} />
                                            <Column field="delta_amount" header={`\u0394 $`} sortable body={(row) => (
                                                <span className={`font-semibold ${row.delta_amount < 0 ? 'text-red-500' : 'text-green-600'}`}>
                                                    {formatMoney(row.delta_amount)}
                                                </span>
                                            )} style={{ width: '100px', textAlign: 'right' }} />
                                            <Column field="delta_pct" header={`\u0394 %`} sortable body={(row) => {
                                                const num = parseFloat(row.delta_pct);
                                                return (
                                                    <Tag
                                                        value={row.delta_pct}
                                                        severity={num < 0 ? 'danger' : 'success'}
                                                    />
                                                );
                                            }} style={{ width: '90px', textAlign: 'right' }} />
                                        </DataTable>
                                    </>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* -- Daily Breakdown (when range > 1 day) -- */}
                    {isMultiDay && dailyBreakdown.length > 0 && (
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center justify-content-between cursor-pointer" onClick={() => toggleSection('dailyBreakdown')}>
                                    <span><i className="pi pi-calendar mr-2" />{appT('Daily Breakdown')} ({dailyBreakdown.length} {appT('days')})</span>
                                    <Button icon={expanded.dailyBreakdown ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} className="p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); toggleSection('dailyBreakdown'); }} />
                                </div>
                            }>
                                {expanded.dailyBreakdown && (
                                    <DataTable
                                        value={dailyBreakdown}
                                        responsiveLayout="scroll"
                                        size="small"
                                        sortField="date"
                                        sortOrder={1}
                                    >
                                        <Column field="date" header={appT('Date')} sortable style={{ width: '100px' }} />
                                        <Column field="driver_names" header={appT('Drivers')} style={{ minWidth: '140px' }} body={(row) => (
                                            <span className="text-sm">{row.driver_names || '-'}</span>
                                        )} />
                                        <Column
                                            header={appT('Machines')}
                                            body={(row) => `${row.machines_serviced} / ${row.machines_planned}`}
                                            style={{ width: '90px', textAlign: 'center' }}
                                        />
                                        <Column
                                            field="compliance_pct"
                                            header={appT('Compliance')}
                                            sortable
                                            body={(row) => <Tag value={formatPct(row.compliance_pct)} severity={complianceSeverity(row.compliance_pct)} />}
                                            style={{ width: '95px', textAlign: 'center' }}
                                        />
                                        <Column field="unserviced" header={appT('Missed')} sortable body={(row) => (
                                            <span className={row.unserviced > 0 ? 'text-red-500 font-semibold' : ''}>{row.unserviced || 0}</span>
                                        )} style={{ width: '70px', textAlign: 'center' }} />
                                        <Column field="duration_display" header={appT('Duration')} style={{ width: '85px', textAlign: 'center' }} />
                                        <Column field="gap_count" header={appT('Gaps')} sortable body={(row) => (
                                            <span className={row.gap_count > 3 ? 'text-orange-500 font-semibold' : ''}>{row.gap_count || '-'}</span>
                                        )} style={{ width: '60px', textAlign: 'center' }} />
                                        <Column field="after_hours_count" header={appT('After Hrs')} sortable body={(row) => (
                                            <span className={row.after_hours_count > 0 ? 'text-red-500 font-semibold' : ''}>{row.after_hours_count || '-'}</span>
                                        )} style={{ width: '75px', textAlign: 'center' }} />
                                        <Column
                                            field="total_sold"
                                            header={appT('Sold')}
                                            sortable
                                            body={(row) => formatMoney(row.total_sold)}
                                            style={{ width: '90px', textAlign: 'right' }}
                                        />
                                        <Column
                                            field="total_over_short"
                                            header={appT('O/S')}
                                            sortable
                                            body={(row) => (
                                                <span className={row.total_over_short < 0 ? 'text-red-500' : row.total_over_short > 0 ? 'text-green-500' : ''}>
                                                    {formatMoney(row.total_over_short)}
                                                </span>
                                            )}
                                            style={{ width: '85px', textAlign: 'right' }}
                                        />
                                    </DataTable>
                                )}
                            </Card>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ParlevelDashboard;
